import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import CustomFieldEnumMultipleEdit from '@atom/components/common/customFieldEnumEdit/customFieldEnumMultipleEdit';
import CustomFieldEnumNestedEdit from '@atom/components/common/customFieldEnumEdit/CustomFieldEnumNestedEdit';
import CustomFieldEnumRootEdit from '@atom/components/common/customFieldEnumEdit/CustomFieldEnumRootEdit';
import FieldViewAsToggle from '@atom/components/common/fieldViewAsToggle/FieldViewAsToggle';
import { Icon, IconButton, Modal, Select, TextField } from '@atom/mui';
import { ArrowBackIcon } from '@atom/mui/icons';
import { customFieldUnitsSelector } from '@atom/selectors/preferencesSelectors';
import { DataType, FieldViewType } from '@atom/types/dataType';
import {
  SubField,
  TEMPLATE_VALUE_WORK_TYPES,
  ValueDictionary,
  WORK_ORDER_FIELD_DATA_TYPE_OPTIONS,
  WorkOrderDropdownField,
  WorkOrderField,
  WorkOrderFieldUpdatePayload,
} from '@atom/types/work';
import {
  isNilOrEmpty,
  isValidUrlWithMessage,
  MAX_HYPERLINK_CHARS,
  VALIDATE_HYPERLINK_DEBOUNCE_MS,
} from '@atom/utilities/validationUtilities';
import {
  getActiveSubFieldPathLabel,
  getNestedDepth,
  getPath,
  hydrateDropdownField,
  isOverListOptionLimit,
  mapDropdownField,
  MAX_LIST_OPTIONS,
  MIN_SINGLE_SELECT_OPTIONS,
  removeSubFieldByMatchValue,
  updateSubFields,
  VIEW_AS_DATA_TYPES,
} from '@atom/utilities/workOrderFieldUtilities';

import WorkOrderModalRequiredFields from './WorkOrderModalRequiredFields';

import classes from './workOrderFields.module.css';

const { MenuItem } = Select;

const enum ModalView {
  ROOT = 'ROOT',
  NESTED = 'NESTED',
}

const styles = {
  inputContainer: {
    marginTop: '1rem',
    marginBottom: '2rem',
  },
  inputContainerInline: {
    display: 'inline-block',
    width: '48%',
    marginTop: '1rem',
    marginRight: '1rem',
  },
  fieldTypeSelect: {
    width: '65%',
  },
  unitSelect: {
    display: 'inline-block',
    width: '48%',
    marginTop: '1rem',
    textTransform: 'uppercase',
  },
  contentStyle: {
    padding: '3rem',
    overflow: 'auto',
  },
  upper: {
    textTransform: 'uppercase',
  },
};

interface Props {
  fields: WorkOrderField[];
  open: boolean;
  field: WorkOrderField | null;
  onClose: () => void;
  handleUpdate: (payload: WorkOrderFieldUpdatePayload) => void;
  loadingUpdate: boolean;
  isTitleDisabled?: boolean;
  isWorkTemplate?: boolean;
  errorText?: string;
}

const WorkOrderFieldUpdateModal = ({
  fields,
  open,
  field,
  onClose,
  handleUpdate,
  loadingUpdate,
  isTitleDisabled = false,
  isWorkTemplate = false,
  errorText = '',
}: Props) => {
  const unitTypes = useSelector(customFieldUnitsSelector);

  const [view, setView] = useState<ModalView>(ModalView.ROOT);
  const [hyperlink, setHyperlink] = useState<string>(field?.hyperlink || '');
  const [urlErrorMsg, setUrlErrorMsg] = useState<string>('');
  const [title, setTitle] = useState<string>(field?.title || '');
  const [value, setValue] = useState<string>(
    TEMPLATE_VALUE_WORK_TYPES.includes(field?.dataType) ? field?.value : null,
  );
  const [dropdownField, setDropdownField] = useState<WorkOrderDropdownField>(
    field?.enumeration
      ? {
          enumeration: field.enumeration,
          subFields: field.subFields,
        }
      : null,
  );
  const [activeKey, setActiveKey] = useState<string>();
  const [activePath, setActivePath] = useState<any[]>([]);

  const [valueDictionary, setValueDictionary] = useState<ValueDictionary>();
  const [enumeration, setEnumeration] = useState<string[]>(
    field?.enumeration || [],
  );
  const [subFields, setSubFields] = useState<SubField[]>(
    field?.subFields || [],
  );
  const [units, setUnits] = useState<string>(field?.units || '');
  const [viewAs, setViewAs] = useState<FieldViewType | null>(
    field?.viewAs ?? null,
  );

  // Required on Create only applies when creating new WO
  // - required is that standard "Required on complete"
  const [requiredOnCreate, setRequiredOnCreate] = useState<boolean>(
    field?.requiredOnCreate || false,
  );
  const [required, setRequired] = useState<boolean>(field?.required || false);

  // For Multiselect fields only
  // - Default required field require only one selection
  const [selectAllOnCreateWork, setSelectAllOnCreateWork] = useState<boolean>(
    field?.selectAllOnCreateWork || false,
  );
  const [selectAllOnCompleteWork, setSelectAllOnCompleteWork] =
    useState<boolean>(field?.selectAllOnCompleteWork || false);

  useEffect(() => {
    if (open) {
      setTitle(field?.title || '');
      setHyperlink(field?.hyperlink || '');
      setValue(
        TEMPLATE_VALUE_WORK_TYPES.includes(field?.dataType)
          ? field?.value
          : null,
      );
      setUnits(field?.units || '');
      setViewAs(field?.viewAs ?? null);
      setRequiredOnCreate(field?.requiredOnCreate || false);
      setRequired(field?.required || false);
      setSelectAllOnCreateWork(field?.selectAllOnCreateWork || false);
      setSelectAllOnCompleteWork(field?.selectAllOnCompleteWork || false);
      setView(ModalView.ROOT);
      setActivePath([]);
      setActiveKey(null);

      if (field?.enumeration) {
        const initialDropdownField = {
          enumeration: field.enumeration,
          subFields: field.subFields,
        };

        // Transforms field tree into mapped field tree with value
        // dictionary for ease of create and edit.
        const mappedField = mapDropdownField(initialDropdownField);

        setValueDictionary(mappedField.valueDictionary);
        setEnumeration(mappedField.enumeration);
        setSubFields(mappedField.subFields);
        setDropdownField(initialDropdownField);
      }
    }
  }, [open]);

  // Set Multiselect Select All options to false
  // if parent required option is ever false
  useEffect(() => {
    if (!requiredOnCreate) {
      setSelectAllOnCreateWork(false);
    }
  }, [requiredOnCreate]);
  useEffect(() => {
    if (!required) {
      setSelectAllOnCompleteWork(false);
    }
  }, [required]);

  // Applies value dictionary to mapped field tree to produce
  // the properly hydrated field tree.
  useEffect(() => {
    const hydratedField = hydrateDropdownField(
      { enumeration, subFields },
      valueDictionary,
      required,
      requiredOnCreate,
    );

    setDropdownField(hydratedField);
  }, [enumeration, subFields, valueDictionary, required, requiredOnCreate]);

  useEffect(() => {
    const newActivePath = getPath(activeKey, subFields);
    const newView =
      R.length(newActivePath) === 0 ? ModalView.ROOT : ModalView.NESTED;

    setActivePath(newActivePath);
    setView(newView);
  }, [activeKey]);

  const isNameInUse = useMemo(() => {
    return fields
      .filter(({ id }) => id !== field?.id)
      .some(
        // eslint-disable-next-line @typescript-eslint/no-shadow
        field => field.title.toLowerCase() === title.toLowerCase(),
      )
      ? 'This name is already in use.'
      : '';
  }, [title, field, fields]);

  const isValid = useMemo(() => {
    const validEnums = dropdownField?.enumeration.reduce(
      (acc, option) => (!R.isEmpty(option.trim()) ? acc + 1 : acc),
      0,
    );

    const isNameValid = isWorkTemplate || !isNameInUse;
    const [isURLValid, errorMsg] = isValidUrlWithMessage(hyperlink);
    setUrlErrorMsg(errorMsg);
    const isHyperlinkValid =
      field?.dataType === DataType.HYPERLINK
        ? !R.isEmpty(hyperlink) &&
          isURLValid &&
          !R.isEmpty(value) &&
          value?.length <= MAX_HYPERLINK_CHARS
        : true;

    return (
      isNameValid &&
      isHyperlinkValid &&
      !R.isEmpty(title) &&
      (field?.dataType !== DataType.ENUM_SINGLE ||
        validEnums >= MIN_SINGLE_SELECT_OPTIONS)
    );
  }, [title, value, hyperlink, isNameInUse, dropdownField, isWorkTemplate]);

  const showViewAsToggle = VIEW_AS_DATA_TYPES.includes(field?.dataType);

  // Options linked to child dropdowns can't be displayed as a list
  const hasCascadingDropdowns = !isNilOrEmpty(subFields);

  // The option limit only applies while the field is displayed as a list
  const isListView =
    showViewAsToggle &&
    !hasCascadingDropdowns &&
    viewAs === FieldViewType.CHECKBOX;
  const isOverListLimit =
    isListView && isOverListOptionLimit(enumeration.length);

  const handleSubmit = () => {
    handleUpdate({
      fieldId: field.id,
      value,
      ...R.reject(isNilOrEmpty, {
        title: isTitleDisabled ? null : title,
        enumeration: dropdownField?.enumeration,
        subFields: dropdownField?.subFields,
        hyperlink,
        required,
        requiredOnCreate,
        selectAllOnCompleteWork,
        selectAllOnCreateWork,
      }),
      ...(field.dataType === DataType.NUMBER && {
        units,
      }),
      // Sent outside the reject above since an empty string clears a list view back to dropdown
      ...(showViewAsToggle && {
        viewAs: hasCascadingDropdowns ? '' : (viewAs ?? ''),
      }),
    });
  };

  const debouncedSetHyperlink = useMemo(
    () =>
      debounce(event => {
        setHyperlink(event.target.value);
      }, VALIDATE_HYPERLINK_DEBOUNCE_MS),
    [],
  );

  const navigateBack = async () => {
    // drops the last 3 entries in the activePath in order
    // to access the parent nested field;
    const parentPath = R.dropLast(3, activePath);
    const parent: SubField = R.view(R.lensPath(parentPath), subFields);
    await setActiveKey(parent.matchValue);
  };

  const updateSubField = (subField: SubField) => {
    setSubFields(updateSubFields(subField, subFields));
  };

  const removeSubField = async (key: string) => {
    await navigateBack();
    setSubFields(removeSubFieldByMatchValue(subFields, key));
  };

  const activeSubField = R.view(R.lensPath(activePath), subFields);
  const nestedDepth = getNestedDepth(activeKey, subFields);
  const pathLabel = getActiveSubFieldPathLabel(
    title,
    activePath,
    subFields,
    valueDictionary,
  );

  const modalTitle =
    view === ModalView.ROOT ? (
      'Edit Field'
    ) : (
      <div className={classes.modalTitle}>
        <IconButton
          onClick={navigateBack}
          size="small"
          data-cy="workOrderFieldUpdateModalBackButton"
        >
          <Icon icon={ArrowBackIcon} />
        </IconButton>
        <div className={classes.modalTitlePath}>{pathLabel}</div>
      </div>
    );

  const error = isWorkTemplate ? errorText : isNameInUse;

  const content = {
    [ModalView.ROOT]: (
      <>
        <Select
          value={field?.dataType}
          style={styles.fieldTypeSelect}
          label="Field Type"
          disabled
          data-cy="workOrderFieldUpdateModalFieldTypeSelect"
        >
          {WORK_ORDER_FIELD_DATA_TYPE_OPTIONS.map(option => (
            <MenuItem key={option.dataType} value={option.dataType}>
              <div className={classes.menuItem}>
                <Icon style={{ marginRight: '0.5rem' }} icon={option.icon} />
                {option.text}
              </div>
            </MenuItem>
          ))}
        </Select>
        {showViewAsToggle && (
          <FieldViewAsToggle
            viewAs={viewAs}
            onChange={setViewAs}
            listDisabled={hasCascadingDropdowns}
            optionCount={enumeration.length}
            dataCy="workOrderFieldUpdateModalViewAsToggle"
          />
        )}
        <TextField
          id="title"
          label="Title"
          value={title}
          onChange={event => setTitle(event.target.value)}
          style={
            field?.dataType === DataType.NUMBER
              ? styles.inputContainerInline
              : styles.inputContainer
          }
          // ensure modal is open to prevent "flash" of error during close animation
          error={open && !!error}
          helperText={open ? error : ''}
          required
          data-cy="workOrderFieldUpdateModalTitleInput"
        />
        {field?.dataType === DataType.SUMMARY && (
          <TextField
            id="description"
            label="Description Text"
            value={value}
            onChange={event => setValue(event.target.value)}
            style={styles.inputContainer}
            maxRows={20}
            multiline
            data-cy="workOrderFieldUpdateModalDescriptionInput"
          />
        )}
        {field?.dataType === DataType.NUMBER && (
          <Select
            label="Unit"
            value={units || ''}
            style={styles.unitSelect}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setUnits(event.target.value)
            }
            data-cy="workOrderFieldUpdateModalUnitSelect"
          >
            <MenuItem value="" />
            {unitTypes.map(type => (
              <MenuItem key={type.unit} value={type.unit} style={styles.upper}>
                {type.unit}
              </MenuItem>
            ))}
          </Select>
        )}
        {field?.dataType === DataType.ENUM_SINGLE && (
          <CustomFieldEnumRootEdit
            maxOptions={isListView ? MAX_LIST_OPTIONS : undefined}
            hideLinkOptions={isListView}
            valueDictionary={valueDictionary}
            setValueDictionary={setValueDictionary}
            enumeration={enumeration}
            setEnumeration={setEnumeration}
            subFields={subFields}
            setSubFields={setSubFields}
            setActiveKey={setActiveKey}
          />
        )}
        {field?.dataType === DataType.ENUM_MULTIPLE && (
          <CustomFieldEnumMultipleEdit
            maxOptions={isListView ? MAX_LIST_OPTIONS : undefined}
            valueDictionary={valueDictionary}
            setValueDictionary={setValueDictionary}
            enumeration={enumeration}
            setEnumeration={setEnumeration}
          />
        )}
        {field?.dataType === DataType.HYPERLINK && (
          <>
            <TextField
              id="hyperlink"
              label="URL"
              defaultValue={hyperlink || field?.hyperlink}
              onChange={debouncedSetHyperlink}
              style={styles.inputContainer}
              error={urlErrorMsg.length > 0}
              helperText={urlErrorMsg}
              required
              data-cy="workOrderFieldUpdateModalUrlInput"
            />
            <TextField
              id="text_to_display"
              label="Text to Display"
              value={value}
              onChange={event => setValue(event.target.value)}
              style={styles.inputContainer}
              helperText={`${value?.length}/${MAX_HYPERLINK_CHARS}`}
              error={value?.length > MAX_HYPERLINK_CHARS}
              slotProps={{ formHelperText: { style: { textAlign: 'right' } } }}
              required
              data-cy="workOrderFieldUpdateModalDisplayTextInput"
            />
          </>
        )}
        {isWorkTemplate &&
          !TEMPLATE_VALUE_WORK_TYPES.includes(field?.dataType) && (
            <WorkOrderModalRequiredFields
              dataType={field?.dataType}
              hasSubFields={!isNilOrEmpty(subFields)}
              requiredOnCreate={requiredOnCreate}
              required={required}
              selectAllOnCreateWork={selectAllOnCreateWork}
              selectAllOnCompleteWork={selectAllOnCompleteWork}
              setSelectAllOnCreateWork={setSelectAllOnCreateWork}
              setSelectAllOnCompleteWork={setSelectAllOnCompleteWork}
              setRequiredOnCreate={setRequiredOnCreate}
              setRequired={setRequired}
            />
          )}
      </>
    ),
    [ModalView.NESTED]: (
      <CustomFieldEnumNestedEdit
        // @ts-ignore
        subField={activeSubField}
        updateSubField={updateSubField}
        valueDictionary={valueDictionary}
        setValueDictionary={setValueDictionary}
        removeSubField={removeSubField}
        setActiveKey={setActiveKey}
        nestedDepth={nestedDepth}
      />
    ),
  };

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={onClose}
      onConfirm={handleSubmit}
      confirmButtonText="Save"
      loading={loadingUpdate}
      disabled={!isValid || isOverListLimit}
      contentStyle={styles.contentStyle}
      disableFooter={view === ModalView.NESTED}
      data-cy="workOrderFieldUpdateModal"
    >
      {content[view]}
    </Modal>
  );
};

export default WorkOrderFieldUpdateModal;
